import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { coachSessionKey } from './useCoachSession'

export interface FocusTarget { index: number | null; category: string; ordinal: number }

/** A just-added block to reveal (scroll-to + flash) once the focus editor
 *  closes. `group` is the destination section (BlockGroup) so the section can
 *  force-expand; `category`+`ordinal` identify the specific card. */
export interface RevealTarget { group: string; category: string; ordinal: number }

interface FocusedBlockCtx {
  target: FocusTarget | null
  open: (t: FocusTarget) => void
  close: () => void
  revealTarget: RevealTarget | null
  requestReveal: (t: RevealTarget) => void
  clearReveal: () => void
}

const Ctx = createContext<FocusedBlockCtx | null>(null)

export function FocusedBlockProvider({ personaId, children }: { personaId: string; children: ReactNode }) {
  const qc = useQueryClient()
  const [target, setTarget] = useState<FocusTarget | null>(null)
  const [revealTarget, setRevealTarget] = useState<RevealTarget | null>(null)
  const open = useCallback((t: FocusTarget) => setTarget(t), [])
  const close = useCallback(() => {
    setTarget(null)
    // Coach turns are persisted per-exchange server-side (persona_coach.py).
    // Invalidate so the shell rail's CoachPanel rehydrates any turns added while
    // the focus editor was open — one source of truth, no divergence.
    void qc.invalidateQueries({ queryKey: coachSessionKey(personaId) })
  }, [qc, personaId])
  const requestReveal = useCallback((t: RevealTarget) => setRevealTarget(t), [])
  const clearReveal = useCallback(() => setRevealTarget(null), [])
  return (
    <Ctx.Provider value={{ target, open, close, revealTarget, requestReveal, clearReveal }}>
      {children}
    </Ctx.Provider>
  )
}

export function useFocusedBlock(): FocusedBlockCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useFocusedBlock must be used within FocusedBlockProvider')
  return ctx
}
